import type { KeyboardEvent } from 'react'
import { useIsDesktop } from './useIsDesktop'

/**
 * Discord-style composer keys: Enter sends, Shift+Enter inserts a newline.
 *
 * Desktop only. A touch keyboard has no Shift+Enter, so binding Enter to send
 * on a phone would leave no way to write a second line at all — there, Enter
 * stays a newline and the Send button is the only way to send. `useIsDesktop`
 * is the same md-breakpoint signal the two-column layout uses.
 *
 * Ignores Enter while an IME composition is open (Japanese, Chinese, Korean
 * input): that Enter is committing the candidate word, not finishing a message,
 * and sending on it truncates what the user was typing.
 */
export function useSendOnEnter(send: () => void, canSend: boolean) {
  const isDesktop = useIsDesktop()

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (!isDesktop) return
    if (e.key !== 'Enter' || e.shiftKey) return
    // `isComposing` lives on the native event; React's synthetic event doesn't
    // surface it. `keyCode === 229` is the older cross-browser tell.
    const native = e.nativeEvent as unknown as { isComposing?: boolean; keyCode?: number }
    if (native.isComposing || native.keyCode === 229) return
    // Swallow the newline even when we can't send (empty draft, over limit,
    // mid-send) — otherwise Enter appends a blank line to a message the user
    // just tried to send, which reads as the app ignoring them.
    e.preventDefault()
    if (canSend) send()
  }

  return { onKeyDown, isDesktop }
}

/** Hint shown under a composer that sends on Enter. Desktop-only, like the key. */
export const SEND_ON_ENTER_HINT = 'Enter to send · Shift+Enter for a new line'
